import {v, useSelector, createAction, assertPartialSchema, setSessionStorage} from '../../../lib'

// SELECTOR
export const selectAuth = () => {
  return useSelector((state) => state?.auth || null)
}
export const selectAccessToken = () => {
  return useSelector((state) => state?.auth?.accessToken || null)
}
// ACTIONS
export const AUTH_SET = 'AUTH_SET'
export const doAUTH_SET = createAction(AUTH_SET)

export const AUTH_REFRESHING = 'AUTH_REFRESHING'
export const doAUTH_REFRESHING = createAction(AUTH_REFRESHING)

export const AUTH_REFRESHING_FAILURE = 'AUTH_REFRESHING_FAILURE'
export const doAUTH_REFRESHING_FAILURE = createAction(AUTH_REFRESHING_FAILURE)

// a refresh failed for a reason that is not the end of the session (network, 5xx, timeout);
// the token is kept and a retry is scheduled for `nextRetryAt`
export const AUTH_REFRESH_UNAVAILABLE = 'AUTH_REFRESH_UNAVAILABLE'
export const doAUTH_REFRESH_UNAVAILABLE = createAction(AUTH_REFRESH_UNAVAILABLE)

// a signal for the retry scheduler to stop waiting; it carries no state of its own
export const AUTH_REFRESH_RETRY_NOW = 'AUTH_REFRESH_RETRY_NOW'
export const doAUTH_REFRESH_RETRY_NOW = createAction(AUTH_REFRESH_RETRY_NOW)

export const LOGOUT = 'LOGOUT'
export const doLOGOUT = createAction(LOGOUT)

export const authActionCreators = {
  doAUTH_SET,
  doAUTH_REFRESHING,
  doAUTH_REFRESHING_FAILURE,
  doAUTH_REFRESH_UNAVAILABLE,
  doAUTH_REFRESH_RETRY_NOW,
  doLOGOUT,
}

// REDUCER
export const authDefaultState = {
  accessToken: null,
  isRefreshing: false,
  accessTokenExpireTimestamp: 0,
  refreshTokenExpireTimestamp: 0,
  // `isRefreshing` means "a refresh request is in flight"; the three fields below mean
  // "refreshing is failing transiently and a retry is scheduled", which outlives one attempt
  refreshUnavailable: false,
  refreshRetryAttempt: 0,
  refreshNextRetryAt: null,
}

const transientRefreshDefaultState = {
  refreshUnavailable: false,
  refreshRetryAttempt: 0,
  refreshNextRetryAt: null,
}

export const authReducer = (state = {...authDefaultState}, {type, payload}) => {
  switch (type) {
    case AUTH_SET: {
      assertPartialSchema({
        payload,
        schema: v.object({
          accessToken: v.string().exist(),
          accessTokenExpiresInSeconds: v.number().exist(),
          refreshTokenExpiresInSeconds: v.number().exist(),
        }),
      })

      const {accessToken, accessTokenExpiresInSeconds, refreshTokenExpiresInSeconds} = payload
      const now = new Date().getTime()

      const auth = {
        accessToken,
        accessTokenExpireTimestamp: now + accessTokenExpiresInSeconds * 1000,
        refreshTokenExpireTimestamp: now + refreshTokenExpiresInSeconds * 1000,
        isRefreshing: false,
        ...transientRefreshDefaultState,
      }

      setSessionStorage({auth})
      return auth
    }

    case AUTH_REFRESHING:
      // deliberately leaves the transient fields alone so a reconnecting notice stays mounted
      // while the scheduled attempt is in flight
      if (!state.isRefreshing) return {...state, isRefreshing: true}
      else return state

    case AUTH_REFRESH_UNAVAILABLE: {
      assertPartialSchema({
        payload,
        schema: v.object({
          attempt: v.number().integer().min(1).exist(),
          nextRetryAt: v.number().exist(),
        }),
      })

      const {attempt, nextRetryAt} = payload
      return {
        ...state,
        isRefreshing: false,
        refreshUnavailable: true,
        refreshRetryAttempt: attempt,
        refreshNextRetryAt: nextRetryAt,
      }
    }

    case AUTH_REFRESHING_FAILURE:
      // terminal: drop the persisted auth entry too, so a reload on the login page cannot
      // restore a token the backend has already retired
      setSessionStorage({auth: null})
      return {...state, accessToken: null, isRefreshing: false, ...transientRefreshDefaultState}

    case LOGOUT:
      return authDefaultState
    default:
      return state
  }
}
